import { useState, useEffect, useCallback } from "react";
import { api } from "../../api/client";
import { useContacts } from "../../contexts/ContactsContext";

// ── Styles ─────────────────────────────────────────────────────────────────

const btnPrimary = {
  padding: "0.55rem 1.2rem", background: "#E4E69B", border: "none",
  borderRadius: 7, fontWeight: 700, fontSize: "0.9rem", cursor: "pointer",
  fontFamily: "Raleway, sans-serif", color: "#0C2D48",
};
const btnSecondary = {
  padding: "0.45rem 1rem", background: "transparent", border: "1px solid #ddd",
  borderRadius: 7, fontWeight: 600, fontSize: "0.85rem", cursor: "pointer",
  fontFamily: "Raleway, sans-serif", color: "#444",
};
const btnDanger = {
  ...btnSecondary, color: "#c0392b", borderColor: "#e8c0bb",
};
const inputStyle = {
  padding: "0.5rem 0.7rem", borderRadius: 7, border: "1px solid #ddd",
  fontSize: "0.88rem", fontFamily: "Raleway, sans-serif",
  background: "#fff", color: "#333", width: "100%", boxSizing: "border-box",
};
const labelStyle = {
  display: "block", fontSize: "0.8rem", fontWeight: 600,
  color: "#555", marginBottom: 4, fontFamily: "Poppins, sans-serif",
};

const FREQUENCIES = ["weekly", "monthly", "quarterly", "annually"];
const FREQ_LABEL = { weekly: "Weekly", monthly: "Monthly", quarterly: "Quarterly", annually: "Annually" };

const EMPTY_FORM = {
  title: "", contactId: "", lineItems: [{ description: "", qty: 1, unitPrice: "" }],
  frequency: "monthly", startDate: "", endDate: "", autoSend: false,
  currency: "GBP", taxRate: 0, notes: "",
};

// ── Helpers ────────────────────────────────────────────────────────────────

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function lineTotal(items = []) {
  return items.reduce((s, li) => s + Number(li.qty || 0) * Number(li.unitPrice || 0), 0);
}

function fmtMoney(n, currency = "GBP") {
  const sym = currency === "USD" ? "$" : currency === "EUR" ? "€" : "£";
  return `${sym}${Number(n).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ── RecurringInvoiceModal ──────────────────────────────────────────────────

function RecurringInvoiceModal({ initial, contacts, onSave, onClose }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  function setLineItem(idx, k, v) {
    setForm(f => {
      const items = [...f.lineItems];
      items[idx] = { ...items[idx], [k]: v };
      return { ...f, lineItems: items };
    });
  }
  function addLine() {
    setForm(f => ({ ...f, lineItems: [...f.lineItems, { description: "", qty: 1, unitPrice: "" }] }));
  }
  function removeLine(idx) {
    setForm(f => ({ ...f, lineItems: f.lineItems.filter((_, i) => i !== idx) }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...form,
        taxRate: Number(form.taxRate) || 0,
        lineItems: form.lineItems.map(li => ({
          ...li,
          qty: Number(li.qty) || 1,
          unitPrice: Number(li.unitPrice) || 0,
        })),
        contactId: form.contactId || null,
        endDate: form.endDate || null,
      };
      const isEdit = !!initial?.id;
      const res = await api(
        isEdit ? `/api/invoices/recurring/${initial.id}` : "/api/invoices/recurring",
        { method: isEdit ? "PUT" : "POST", body: JSON.stringify(payload) },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Save failed");
      onSave(data.recurringInvoice, isEdit);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  const subtotal = lineTotal(form.lineItems);
  const tax = subtotal * (Number(form.taxRate) / 100);

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)",
      zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
      padding: "1rem",
    }}>
      <div style={{
        background: "#fff", borderRadius: 12, padding: "2rem",
        width: "100%", maxWidth: 620, maxHeight: "90vh", overflowY: "auto",
        boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
          <h2 style={{ margin: 0, fontFamily: "Poppins, sans-serif", fontSize: "1.15rem", fontWeight: 700 }}>
            {initial?.id ? "Edit recurring invoice" : "New recurring invoice"}
          </h2>
          <button onClick={onClose} style={{ ...btnSecondary, padding: "0.3rem 0.7rem" }}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Title */}
          <div style={{ marginBottom: "1rem" }}>
            <label style={labelStyle}>Title *</label>
            <input style={inputStyle} value={form.title} onChange={e => set("title", e.target.value)} required placeholder="e.g. Monthly retainer" />
          </div>

          {/* Client */}
          <div style={{ marginBottom: "1rem" }}>
            <label style={labelStyle}>Client</label>
            <select style={inputStyle} value={form.contactId} onChange={e => set("contactId", e.target.value)}>
              <option value="">— No client —</option>
              {contacts.map(c => (
                <option key={c.id} value={c.id}>{c.name}{c.company ? ` (${c.company})` : ""}</option>
              ))}
            </select>
          </div>

          {/* Frequency + Currency */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
            <div>
              <label style={labelStyle}>Frequency *</label>
              <select style={inputStyle} value={form.frequency} onChange={e => set("frequency", e.target.value)} required>
                {FREQUENCIES.map(f => <option key={f} value={f}>{FREQ_LABEL[f]}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Currency</label>
              <select style={inputStyle} value={form.currency} onChange={e => set("currency", e.target.value)}>
                {["GBP", "USD", "EUR"].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* Dates */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
            <div>
              <label style={labelStyle}>Start date *</label>
              <input style={inputStyle} type="date" value={form.startDate} onChange={e => set("startDate", e.target.value)} required />
            </div>
            <div>
              <label style={labelStyle}>End date (optional)</label>
              <input style={inputStyle} type="date" value={form.endDate} onChange={e => set("endDate", e.target.value)} />
            </div>
          </div>

          {/* Line items */}
          <div style={{ marginBottom: "1rem" }}>
            <label style={labelStyle}>Line items</label>
            {form.lineItems.map((li, idx) => (
              <div key={idx} style={{ display: "grid", gridTemplateColumns: "1fr 60px 90px 32px", gap: "0.4rem", marginBottom: "0.4rem", alignItems: "center" }}>
                <input style={inputStyle} placeholder="Description" value={li.description} onChange={e => setLineItem(idx, "description", e.target.value)} />
                <input style={{ ...inputStyle, textAlign: "center" }} type="number" min="1" placeholder="Qty" value={li.qty} onChange={e => setLineItem(idx, "qty", e.target.value)} />
                <input style={{ ...inputStyle, textAlign: "right" }} type="number" min="0" step="0.01" placeholder="Price" value={li.unitPrice} onChange={e => setLineItem(idx, "unitPrice", e.target.value)} />
                <button type="button" onClick={() => removeLine(idx)} style={{ ...btnDanger, padding: "0.3rem 0.5rem", fontSize: "0.8rem" }} disabled={form.lineItems.length === 1}>✕</button>
              </div>
            ))}
            <button type="button" onClick={addLine} style={{ ...btnSecondary, marginTop: "0.3rem", fontSize: "0.8rem" }}>+ Add line</button>
          </div>

          {/* Tax rate */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
            <div>
              <label style={labelStyle}>Tax rate (%)</label>
              <input style={inputStyle} type="number" min="0" max="100" step="0.1" value={form.taxRate} onChange={e => set("taxRate", e.target.value)} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", paddingBottom: "0.1rem" }}>
              <span style={{ fontSize: "0.82rem", color: "#666" }}>
                Subtotal: {fmtMoney(subtotal, form.currency)}<br />
                {Number(form.taxRate) > 0 && <>Tax: {fmtMoney(tax, form.currency)}<br /></>}
                <strong>Total: {fmtMoney(subtotal + tax, form.currency)}</strong>
              </span>
            </div>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: "1rem" }}>
            <label style={labelStyle}>Notes</label>
            <textarea style={{ ...inputStyle, minHeight: 70, resize: "vertical" }} value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Internal note or invoice message" />
          </div>

          {/* Auto-send */}
          <div style={{ marginBottom: "1.5rem", display: "flex", alignItems: "center", gap: "0.6rem" }}>
            <input type="checkbox" id="autoSend" checked={form.autoSend} onChange={e => set("autoSend", e.target.checked)} style={{ width: 16, height: 16, cursor: "pointer" }} />
            <label htmlFor="autoSend" style={{ ...labelStyle, margin: 0, cursor: "pointer" }}>
              Auto-send to client when generated
            </label>
          </div>

          {error && <p style={{ color: "#c0392b", fontSize: "0.85rem", marginBottom: "1rem" }}>{error}</p>}

          <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
            <button type="button" onClick={onClose} style={btnSecondary}>Cancel</button>
            <button type="submit" style={btnPrimary} disabled={saving}>
              {saving ? "Saving…" : initial?.id ? "Save changes" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── RecurringInvoiceCard ───────────────────────────────────────────────────

function RecurringInvoiceCard({ item, contacts, onEdit, onDelete, onGenerate, onToggleActive }) {
  const [generating, setGenerating] = useState(false);
  const [toggling, setToggling] = useState(false);

  const contact = contacts.find(c => c.id === item.contactId);
  const subtotal = lineTotal(item.lineItems);

  async function handleGenerate() {
    setGenerating(true);
    try {
      const res = await api(`/api/invoices/recurring/${item.id}/generate`, { method: "POST" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");
      onGenerate(item.id, data.nextDueDate);
    } catch (err) {
      alert(err.message);
    } finally {
      setGenerating(false);
    }
  }

  async function handleToggle() {
    setToggling(true);
    try {
      const res = await api(`/api/invoices/recurring/${item.id}`, {
        method: "PUT", body: JSON.stringify({ isActive: !item.isActive }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");
      onToggleActive(data.recurringInvoice);
    } catch (err) {
      alert(err.message);
    } finally {
      setToggling(false);
    }
  }

  return (
    <div style={{
      background: "#fff", border: `1px solid ${item.isActive ? "#e8eabc" : "#e5e5e5"}`,
      borderRadius: 10, padding: "1.25rem 1.4rem",
      opacity: item.isActive ? 1 : 0.65,
      boxShadow: item.isActive ? "0 1px 6px rgba(0,0,0,0.06)" : "none",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontWeight: 700, fontFamily: "Poppins, sans-serif", fontSize: "1rem", marginBottom: 2 }}>
            {item.title}
          </div>
          {contact && (
            <div style={{ fontSize: "0.85rem", color: "#555", marginBottom: 4 }}>
              {contact.name}{contact.company ? ` · ${contact.company}` : ""}
            </div>
          )}
          <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginTop: 4 }}>
            <span style={{ fontSize: "0.78rem", background: "#f0f1d4", borderRadius: 5, padding: "2px 8px", fontWeight: 600, color: "#4a5000" }}>
              {FREQ_LABEL[item.frequency]}
            </span>
            {item.autoSend && (
              <span style={{ fontSize: "0.78rem", background: "#d4f0e0", borderRadius: 5, padding: "2px 8px", fontWeight: 600, color: "#1a6640" }}>
                Auto-send
              </span>
            )}
            {!item.isActive && (
              <span style={{ fontSize: "0.78rem", background: "#f5f5f5", borderRadius: 5, padding: "2px 8px", fontWeight: 600, color: "#888" }}>
                Paused
              </span>
            )}
          </div>
        </div>

        <div style={{ textAlign: "right", minWidth: 130 }}>
          <div style={{ fontWeight: 700, fontSize: "1.05rem", fontFamily: "Poppins, sans-serif" }}>
            {fmtMoney(subtotal, item.currency)}
          </div>
          <div style={{ fontSize: "0.78rem", color: "#888", marginTop: 2 }}>
            Next: {fmtDate(item.nextDueDate)}
          </div>
          {item.endDate && (
            <div style={{ fontSize: "0.75rem", color: "#aaa" }}>Ends {fmtDate(item.endDate)}</div>
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem", flexWrap: "wrap" }}>
        <button onClick={() => onEdit(item)} style={btnSecondary}>Edit</button>
        <button onClick={handleGenerate} style={btnSecondary} disabled={generating}>
          {generating ? "Generating…" : "Generate now"}
        </button>
        <button onClick={handleToggle} style={btnSecondary} disabled={toggling}>
          {toggling ? "…" : item.isActive ? "Pause" : "Activate"}
        </button>
        <button onClick={() => onDelete(item.id)} style={btnDanger}>Delete</button>
      </div>
    </div>
  );
}

// ── RecurringInvoices (main) ───────────────────────────────────────────────

export default function RecurringInvoices() {
  const { contacts } = useContacts();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api("/api/invoices/recurring");
      const data = await res.json();
      if (Array.isArray(data.recurringInvoices)) setItems(data.recurringInvoices);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function handleSave(saved, isEdit) {
    setItems(prev => isEdit
      ? prev.map(i => i.id === saved.id ? saved : i)
      : [saved, ...prev],
    );
    setShowModal(false);
    setEditing(null);
  }

  function handleDelete(id) {
    if (!window.confirm("Delete this recurring invoice template?")) return;
    api(`/api/invoices/recurring/${id}`, { method: "DELETE" }).then(() => {
      setItems(prev => prev.filter(i => i.id !== id));
    });
  }

  function handleGenerate(id, nextDueDate) {
    setItems(prev => prev.map(i => i.id === id ? { ...i, nextDueDate } : i));
  }

  function handleToggleActive(updated) {
    setItems(prev => prev.map(i => i.id === updated.id ? updated : i));
  }

  function openEdit(item) {
    setEditing({
      ...item,
      startDate: item.startDate?.slice(0, 10) || "",
      endDate: item.endDate?.slice(0, 10) || "",
      taxRate: item.taxRate ?? 0,
      lineItems: item.lineItems?.length ? item.lineItems : [{ description: "", qty: 1, unitPrice: "" }],
    });
    setShowModal(true);
  }

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: "1.5rem 1rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
        <div>
          <h2 style={{ margin: 0, fontFamily: "Poppins, sans-serif", fontSize: "1.3rem", fontWeight: 700 }}>
            Recurring invoices
          </h2>
          <p style={{ margin: "0.3rem 0 0", fontSize: "0.85rem", color: "#666" }}>
            Templates that auto-generate invoices on a schedule.
          </p>
        </div>
        <button style={btnPrimary} onClick={() => { setEditing(null); setShowModal(true); }}>
          + New recurring
        </button>
      </div>

      {loading && <p style={{ color: "#888", fontFamily: "Raleway, sans-serif" }}>Loading…</p>}

      {!loading && items.length === 0 && (
        <div style={{
          textAlign: "center", padding: "3rem 1rem", border: "2px dashed #e5e5e5",
          borderRadius: 12, color: "#aaa", fontFamily: "Raleway, sans-serif",
        }}>
          <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>🔁</div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>No recurring invoices yet</div>
          <div style={{ fontSize: "0.85rem" }}>Create a template and Aidey will generate invoices automatically.</div>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "0.9rem" }}>
        {items.map(item => (
          <RecurringInvoiceCard
            key={item.id}
            item={item}
            contacts={contacts}
            onEdit={openEdit}
            onDelete={handleDelete}
            onGenerate={handleGenerate}
            onToggleActive={handleToggleActive}
          />
        ))}
      </div>

      {showModal && (
        <RecurringInvoiceModal
          initial={editing}
          contacts={contacts}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditing(null); }}
        />
      )}
    </div>
  );
}
